'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import addonService from '../services/addonService';
import { CHECKOUT, SUBSCRIPTION } from '../constants/routes';
import subscriptionCostService, { parseSubscriptionCost } from '../services/subscriptionCostService';
import PurchaseStepper from '../components/PurchaseStepper';

interface SelectedSport {
  id: string;
  name: string;
  icon: string;
  gender: 'M' | 'W' | null;
}

interface AddonState {
  [sportId: string]: {
    statCrew: boolean;
    seasons: string[];
  };
}

type AddonMeta =
  | { status: 'idle' }
  | { status: 'loading'; sportCode: string }
  | {
      status: 'available';
      sportCode: string;
      addonTypeId: number;
      addonName: string;
      seasons: string[];
      ownedSeasons: string[];
      costPerSeason: number;
      seasonPrices: Record<string, number>;
    }
  | { status: 'none'; sportCode: string }
  | { status: 'error'; sportCode: string; message: string };

const ALL_SEASONS = 'all';

const SUBSCRIPTION_ID_TO_SPORT_CODE: Record<string, string> = {
  football: 'MFB',
  'basketball-m': 'MBB',
  'basketball-w': 'WBB',
  'ice-hockey-m': 'MIH',
  'ice-hockey-w': 'WIH',
  'soccer-m': 'MSO',
  'soccer-w': 'WSO',
  'volleyball-m': 'MVB',
  'volleyball-w': 'WVB',
};

export default function Addons() {
  const navigate = useNavigate();
  const location = useLocation();

  const asFiniteNumber = (value: any): number | null => {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const selectedSports: SelectedSport[] = location.state?.selectedSports || [];
  const isAddonOnly: boolean = Boolean(location.state?.isAddonOnly);
  const [basePriceBySportId, setBasePriceBySportId] = useState<Record<string, number>>(
    location.state?.basePriceBySportId || {}
  );
  const [currency, setCurrency] = useState<string | null>(location.state?.currency || null);
  const [pricingUnavailable, setPricingUnavailable] = useState(false);
  const [pricingLoading, setPricingLoading] = useState(false);

  const [isBuffering, setIsBuffering] = useState(true);
  const [openSportIds, setOpenSportIds] = useState<Record<string, boolean>>({});
  const [addons, setAddons] = useState<AddonState>(() => location.state?.addons || {});
  const [addonMetaBySportId, setAddonMetaBySportId] = useState<Record<string, AddonMeta>>(() => location.state?.addonMetaBySportId || {});
  const [continueError, setContinueError] = useState<string | null>(null);
  const [removedSportIds, setRemovedSportIds] = useState<Set<string>>(new Set());
  const [hoveredSeasonBySportId, setHoveredSeasonBySportId] = useState<Record<string, string | null>>({});

  const getBasePrice = (sportId: string): number => {
    const v = Number(basePriceBySportId?.[sportId]);
    return Number.isFinite(v) ? v : NaN;
  };

  const baseCost = useMemo(() => {
    return selectedSports.reduce((sum, sport) => sum + (Number(getBasePrice(sport.id)) || 0), 0);
  }, [basePriceBySportId, selectedSports]);

  // Filter out removed sports
  const displayedSports = useMemo(() => {
    return selectedSports.filter((sport) => !removedSportIds.has(sport.id));
  }, [selectedSports, removedSportIds]);

  const getSeasonsForSport = (sportId: string): string[] => {
    const meta = addonMetaBySportId[sportId];
    return meta?.status === 'available' ? meta.seasons : [];
  };

  const getOwnedSeasonsForSport = (sportId: string): string[] => {
    const meta = addonMetaBySportId[sportId];
    return meta?.status === 'available' ? meta.ownedSeasons || [] : [];
  };

  const getSelectedSeasons = (sportId: string): string[] => {
    const selected = addons[sportId]?.seasons || [];
    const owned = new Set(getOwnedSeasonsForSport(sportId));
    if (selected.includes(ALL_SEASONS)) {
      return getSeasonsForSport(sportId).filter((s) => !owned.has(String(s)));
    }
    return selected.filter((s) => s !== ALL_SEASONS && !owned.has(String(s)));
  };

  const getAddonTotal = (sportId: string): number => {
    const addon = addons[sportId];
    const meta = addonMetaBySportId[sportId];
    if (!addon?.statCrew) return 0;
    if (meta?.status !== 'available') return 0;

    const seasons = getSelectedSeasons(sportId);
    if (seasons.length === 0) return 0;

    return seasons.reduce(
      (sum, s) => sum + (Number(meta.seasonPrices?.[s]) || Number(meta.costPerSeason) || 0),
      0
    );
  };

  const totalAddonCost = useMemo(
    () => selectedSports.reduce((sum, sport) => sum + getAddonTotal(sport.id), 0),
    [addons, addonMetaBySportId, selectedSports]
  );

  const subscribedSports = useMemo(
    () => selectedSports.filter((s) => getBasePrice(s.id) === 0),
    [basePriceBySportId, selectedSports]
  );

  const missingAddonSelectionForSubscribed = subscribedSports.filter((s) => {
    if (!addons[s.id]?.statCrew) return false;
    const meta = addonMetaBySportId[s.id];
    if (meta?.status !== 'available') return false;
    const seasons = getSelectedSeasons(s.id);
    return seasons.length === 0;
  });

  const hasAnyAddonSelection = useMemo(() => {
    return selectedSports.some((s) => addons[s.id]?.statCrew && getSelectedSeasons(s.id).length > 0);
  }, [addons, addonMetaBySportId, selectedSports]);

  // Get new sports (not already subscribed - base price > 0)
  const newSports = useMemo(
    () => selectedSports.filter((s) => getBasePrice(s.id) > 0),
    [basePriceBySportId, selectedSports]
  );

  // Validation logic: at least 1 new sport OR at least 1 addon must be selected
  const validateAddonSelection = (): string | null => {
    // If new sport is selected, allow checkout (addon is optional)
    if (newSports.length > 0) {
      return null;
    }

    // If no new sports, at least one addon must be selected
    if (hasAnyAddonSelection) {
      return null;
    }

    // Neither new sport nor addon selected
    return 'Please select at least one new sport or addon to continue.';
  };

  const canContinueToCheckout =
    displayedSports.length > 0 &&
    !pricingUnavailable &&
    !pricingLoading;

  useEffect(() => {
    let cancelled = false;

    async function ensurePricing() {
      if (!selectedSports.length) return;

      const missing = selectedSports.filter((s) => !Number.isFinite(Number(basePriceBySportId?.[s.id])));
      if (missing.length === 0) return;

      setPricingLoading(true);
      setPricingUnavailable(false);

      for (const sport of missing) {
        const sportCode = SUBSCRIPTION_ID_TO_SPORT_CODE[sport.id];
        if (!sportCode) {
          if (!cancelled) setPricingUnavailable(true);
          continue;
        }

        if (isAddonOnly) {
          setBasePriceBySportId((prev) => {
            if (prev[sport.id] === 0) return prev;
            return { ...prev, [sport.id]: 0 };
          });
          if (currency) continue;
        }

        const res: any = await subscriptionCostService.getSubscriptionCost(sportCode);
        const status = res?.status;
        if (cancelled) return;

        if (status === 404) {
          setPricingUnavailable(true);
          continue;
        }
        if (!status || status < 200 || status >= 300) {
          setPricingUnavailable(true);
          continue;
        }

        const { cost, currency: cur } = parseSubscriptionCost(res?.data);
        if (cost === null || !cur) {
          setPricingUnavailable(true);
          continue;
        }

        setBasePriceBySportId((prev) => {
          const target = isAddonOnly ? 0 : cost;
          if (prev[sport.id] === target) return prev;
          return { ...prev, [sport.id]: target };
        });
        setCurrency((prev) => {
          if (prev === cur) return prev;
          return cur;
        });
      }

      setPricingLoading(false);
    }

    void ensurePricing();
    return () => {
      cancelled = true;
    };
  }, [currency, isAddonOnly, selectedSports]);

  const toggleStatCrew = (sportId: string) => {
    setAddons((prev) => ({
      ...prev,
      [sportId]: {
        statCrew: !prev[sportId]?.statCrew,
        seasons: prev[sportId]?.seasons || [],
      },
    }));
  };

  const toggleSportOpen = (sportId: string) => {
    setOpenSportIds((prev) => ({ ...prev, [sportId]: !prev[sportId] }));
  };

  const toggleSeason = (sportId: string, season: string) => {
    setAddons((prev) => {
      const available = [...getSeasonsForSport(sportId)].sort((a, b) => Number(b) - Number(a));
      const current = prev[sportId]?.seasons || [];
      const owned = new Set(getOwnedSeasonsForSport(sportId));

      if (season === ALL_SEASONS) {
        const isAllSelected = current.includes(ALL_SEASONS) || current.length === available.filter(s => !owned.has(s)).length;
        return {
          ...prev,
          [sportId]: {
            statCrew: true,
            seasons: isAllSelected ? [] : [ALL_SEASONS],
          },
        };
      }

      if (owned.has(String(season))) return prev;

      const yearIndex = available.indexOf(season);
      if (yearIndex === -1) return prev;

      const isSelected = current.includes(ALL_SEASONS) || current.includes(season);

      let updated: string[];
      if (isSelected) {
        updated = [];
      } else {
        const requiredSeasons = available.slice(0, yearIndex + 1);
        updated = requiredSeasons.filter(s => !owned.has(s));
      }

      return {
        ...prev,
        [sportId]: {
          statCrew: true,
          seasons: updated,
        },
      };
    });
  };

  const fetchStatCrewAddon = async (sportId: string) => {
    const sportCode = SUBSCRIPTION_ID_TO_SPORT_CODE[sportId];
    if (!sportCode) {
      setAddonMetaBySportId((prev) => ({ ...prev, [sportId]: { status: 'none', sportCode: '' } }));
      return;
    }

    setAddonMetaBySportId((prev) => ({ ...prev, [sportId]: { status: 'loading', sportCode } }));

    try {
      const res = await addonService.getStatCrewAddon(sportCode);
	      const addonTypeIdRaw =
	        Number(res?.data?.addon_type_id) ||
	        Number(res?.data?.addonTypeId) ||
	        Number(res?.data?.addon_type?.id) ||
	        Number(res?.data?.addonType?.id);
	      const addonTypeId = Number.isFinite(addonTypeIdRaw) && addonTypeIdRaw > 0 ? addonTypeIdRaw : 1;
	      const rawSeasons = res?.data?.seasons ?? res?.data?.available_seasons ?? res?.data?.availableSeasons;
	      const ownedSeasons: string[] = Array.isArray(res?.data?.owned_seasons)
	        ? res.data.owned_seasons.map((s: any) => String(s))
	        : Array.isArray(res?.data?.ownedSeasons)
	          ? res.data.ownedSeasons.map((s: any) => String(s))
	          : [];

	      const costPerSeasonCandidate =
	        asFiniteNumber(res?.data?.cost_per_season) ??
	        asFiniteNumber(res?.data?.costPerSeason) ??
	        asFiniteNumber(res?.data?.cost) ??
	        asFiniteNumber(res?.data?.price) ??
	        asFiniteNumber(res?.data?.amount);

	      const seasons: string[] = [];
	      const seasonPrices: Record<string, number> = {};

	      // New response: seasons is [{ season: "1997", price: 1 }, ...]
	      const seasonsAsObjects =
	        Array.isArray(rawSeasons) && rawSeasons.length > 0 && rawSeasons.every((s: any) => s && typeof s === 'object');

	      if (Array.isArray(rawSeasons) && rawSeasons.length > 0 && seasonsAsObjects) {
	        rawSeasons.forEach((item: any) => {
	          const seasonKey = item?.season ?? item?.year ?? item?.name;
	          if (seasonKey === null || seasonKey === undefined) return;
	          const seasonStr = String(seasonKey);
	          seasons.push(seasonStr);

	          const price =
	            asFiniteNumber(item?.price) ??
	            asFiniteNumber(item?.cost) ??
	            asFiniteNumber(item?.amount) ??
	            costPerSeasonCandidate;
	          if (price === null) return;
	          seasonPrices[seasonStr] = price;
	        });
	      } else {
	        // Old response: seasons is ["1997", ...] and pricing may come from cost_per_season or season_prices
	        if (Array.isArray(rawSeasons)) {
	          rawSeasons.forEach((s: any) => seasons.push(String(s)));
	        }

	        const rawSeasonPrices =
	          res?.data?.season_prices ?? res?.data?.seasonPrices ?? res?.data?.seasonPricesBySeason;

	        if (Array.isArray(rawSeasonPrices) && rawSeasonPrices.length > 0) {
	          rawSeasonPrices.forEach((p: any) => {
	            const seasonKey = p?.season ?? p?.year ?? p?.name;
	            if (seasonKey === null || seasonKey === undefined) return;
	            const price =
	              asFiniteNumber(p?.price) ??
	              asFiniteNumber(p?.cost) ??
	              asFiniteNumber(p?.amount) ??
	              costPerSeasonCandidate;
	            if (price === null) return;
	            seasonPrices[String(seasonKey)] = price;
	          });
	        } else if (rawSeasonPrices && typeof rawSeasonPrices === 'object') {
	          Object.entries(rawSeasonPrices as Record<string, any>).forEach(([seasonKey, v]) => {
	            const price =
	              asFiniteNumber((v as any)?.price) ??
	              asFiniteNumber((v as any)?.cost) ??
	              asFiniteNumber((v as any)?.amount) ??
	              asFiniteNumber(v) ??
	              costPerSeasonCandidate;
	            if (price === null) return;
	            seasonPrices[String(seasonKey)] = price;
	          });
	        }
	      }

	      let costPerSeason =
	        costPerSeasonCandidate ??
	        (Object.values(seasonPrices).find((n) => Number.isFinite(n)) ?? null) ??
	        0;
	      if (!Number.isFinite(costPerSeason)) costPerSeason = 0;

	      const resolvedSeasons =
	        seasons.length > 0 ? seasons : Object.keys(seasonPrices).map((s) => String(s));
	      resolvedSeasons.forEach((s) => {
	        if (seasonPrices[s] === undefined) seasonPrices[s] = costPerSeason;
	      });

      setAddonMetaBySportId((prev) => ({
        ...prev,
        [sportId]: {
          status: 'available',
          sportCode: String(res?.data?.sport_code || sportCode),
          addonTypeId,
          addonName: String(res?.data?.addon_name || 'Stat Crew'),
          seasons: resolvedSeasons,
          ownedSeasons,
          costPerSeason,
          seasonPrices,
        },
      }));

      setAddons((prev) => ({
        ...prev,
        [sportId]: {
          statCrew: prev?.[sportId]?.statCrew ?? true,
          seasons: (prev?.[sportId]?.seasons || []).filter((s) => !ownedSeasons.includes(String(s))),
        },
      }));
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403 || status === 404) {
        setAddonMetaBySportId((prev) => ({ ...prev, [sportId]: { status: 'none', sportCode } }));
        setAddons((prev) => {
          const next = { ...prev };
          delete next[sportId];
          return next;
        });
        return;
      }

      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to load addons.';
      setAddonMetaBySportId((prev) => ({
        ...prev,
        [sportId]: { status: 'error', sportCode, message: String(message) },
      }));
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setIsBuffering(false), 1000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!selectedSports || selectedSports.length === 0) return;
    selectedSports.forEach((sport) => {
      void fetchStatCrewAddon(sport.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to top when this page mounts
    useEffect(() => {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }, []);

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#E8EBF3] via-[#F0F3FA] to-[#F8FAFC]">
      {/* Back Button */}
      <div className="max-w-6xl mx-auto px-4 pt-6 flex items-start">
        <button
          onClick={() => {
            navigate(SUBSCRIPTION, { state: { selectedSports: displayedSports, addons, addonMetaBySportId, basePriceBySportId, currency, isAddonOnly } });
          }}
          className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
          aria-label="Go back to sports"
        >
          <svg className="w-6 h-6 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>
      <div className="max-w-6xl mx-auto px-4 py-10">
        <PurchaseStepper currentStep={2} className="mb-10" />

        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-slate-900 mb-2 tracking-tight">Add-ons</h1>
          <p className="text-slate-600">Enhance your experience with powerful add-ons.</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <div className="px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm text-sm font-semibold text-slate-800">
              Sports cost:{' '}
              <span className="font-extrabold text-slate-900">
                {currency ? `${baseCost} ${currency}` : `$${baseCost}`}
              </span>
            </div>
            <div className="px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm text-sm font-semibold text-slate-800">
              Total addons cost:{' '}
              <span className="font-extrabold text-slate-900">${totalAddonCost.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {pricingUnavailable && (
          <div className="max-w-3xl mx-auto mb-6">
            <div className="bg-white border border-amber-200 rounded-xl px-5 py-4 text-amber-800 font-primary">
              Pricing unavailable, contact support.
            </div>
          </div>
        )}

        {isBuffering && (
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
              <div className="inline-flex items-center gap-3 text-slate-700 font-semibold">
                <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                Loading addons…
              </div>
            </div>
          </div>
        )}

        {!isBuffering && selectedSports.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center text-slate-700">
            No sports selected. Go back and choose at least one sport.
          </div>
        ) : !isBuffering && displayedSports.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center text-slate-700">
            All sports have been removed. <br />
            <button
              onClick={() => navigate(SUBSCRIPTION, { state: { selectedSports, addons, addonMetaBySportId, basePriceBySportId, currency, isAddonOnly } })}
              className="text-red-600 hover:text-red-700 font-semibold mt-2"
            >
              Go back to select sports
            </button>
          </div>
        ) : !isBuffering ? (
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              {/* Card header */}
              <div className="px-6 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center">
                    <span className="text-red-600 font-bold">📊</span>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-slate-900">Stat Crew</p>
                    <p className="text-sm text-slate-500">Select seasons per sport</p>
                  </div>
                </div>
	                <div className="text-right">
	                  <p className="text-slate-900 font-bold">
	                    {(() => {
	                      const first = selectedSports[0];
	                      const meta = first ? addonMetaBySportId[first.id] : null;
	                      if (!meta || meta.status !== 'available') return '$0/per season';
	                      const prices = Object.values(meta.seasonPrices || {}).filter((p) => Number.isFinite(Number(p)));
	                      if (prices.length === 0) return `$${meta.costPerSeason || 0}/per season`;
	                      const firstPrice = prices[0];
	                      const uniform = prices.every((p) => p === firstPrice);
	                      return uniform ? `$${firstPrice}/per season` : 'Varies by season';
	                    })()}
	                  </p>
	                </div>
	              </div>
              <div className="border-t border-slate-200" />

              {/* Sports rows */}
	              {displayedSports.map((sport) => {
	                const meta = addonMetaBySportId[sport.id] || { status: 'idle' as const };
	                const isOpen = openSportIds[sport.id] || false;
	                const allSeasons = getSeasonsForSport(sport.id);
	                const ownedSeasons = new Set(getOwnedSeasonsForSport(sport.id));
	                const visibleSeasons = allSeasons;
	                const addonTotal = getAddonTotal(sport.id);
	                const selected = addons[sport.id]?.seasons || [];
	                const isSubscribedSport = getBasePrice(sport.id) === 0;
	                const isMissingSubscribedSelection =
                  isSubscribedSport &&
                  addons[sport.id]?.statCrew &&
                  meta.status === 'available' &&
                  getSelectedSeasons(sport.id).length === 0;

                return (
                  <div key={sport.id} className="px-6 py-5 border-b last:border-b-0 border-slate-100">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="text-2xl">{sport.icon}</div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-bold text-slate-900 truncate">{sport.name}</p>
                            {sport.gender && (
                              <span
                                className={`inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-bold ${
                                  sport.gender === 'M'
                                    ? 'bg-cyan-100 text-cyan-700'
                                    : 'bg-pink-100 text-pink-700'
                                }`}
                              >
                                {sport.gender}
                              </span>
                            )}
                          </div>
                          {meta.status === 'loading' && (
                            <p className="text-xs text-slate-500 mt-1">Loading seasons…</p>
                          )}
                          {meta.status === 'none' && (
                            <p className="text-xs text-slate-500 mt-1">No addons for this sport.</p>
                          )}
                          {meta.status === 'error' && (
                            <p className="text-xs text-red-600 mt-1">{meta.message}</p>
                          )}
                          {isMissingSubscribedSelection && continueError && (
                            <p className="text-xs text-red-600 mt-1">
                              Select at least 1 season (required for subscribed sports).
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-slate-900"> ${addonTotal}</p>
                        </div>
                        {/* Close button to remove addon */}
                        <button
                          type="button"
                          onClick={() => {
                            setRemovedSportIds((prev) => new Set([...prev, sport.id]));
                            setAddons((prev) => {
                              const next = { ...prev };
                              delete next[sport.id];
                              return next;
                            });
                          }}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                          title="Remove addon"
                          aria-label="Remove addon"
                        >
                          <svg className="w-5 h-5 text-red-500 hover:text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Season chips */}
                    {meta.status === 'available' && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {(() => {
                          const sortedVisible = [...visibleSeasons].sort((a, b) => Number(b) - Number(a));
                          const hoveredSeason = hoveredSeasonBySportId[sport.id] || null;
                          const hoverIndex = hoveredSeason ? sortedVisible.indexOf(hoveredSeason) : -1;

                          return (
                            <>
                              <button
                                type="button"
                                onClick={() => toggleSeason(sport.id, ALL_SEASONS)}
                                onMouseEnter={() => {
                                  const oldest = sortedVisible[sortedVisible.length - 1];
                                  if (oldest) {
                                    setHoveredSeasonBySportId((prev) => ({ ...prev, [sport.id]: oldest }));
                                  }
                                }}
                                onMouseLeave={() => {
                                  setHoveredSeasonBySportId((prev) => ({ ...prev, [sport.id]: null }));
                                }}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
                                  selected.includes(ALL_SEASONS)
                                    ? 'bg-red-500 text-white border-red-500 hover:bg-red-600'
                                    : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                                }`}
                              >
                                All years
                              </button>
                              {sortedVisible.map((season) => {
                                const isSelected = selected.includes(ALL_SEASONS) || selected.includes(season);
                                const isOwned = ownedSeasons.has(String(season));
                                const seasonIndex = sortedVisible.indexOf(season);
                                const isHovered = hoverIndex !== -1 && seasonIndex <= hoverIndex;

                                return (
                                  <button
                                    key={season}
                                    type="button"
                                    onClick={() => toggleSeason(sport.id, season)}
                                    onMouseEnter={() => {
                                      setHoveredSeasonBySportId((prev) => ({ ...prev, [sport.id]: season }));
                                    }}
                                    onMouseLeave={() => {
                                      setHoveredSeasonBySportId((prev) => ({ ...prev, [sport.id]: null }));
                                    }}
                                    disabled={isOwned}
                                    title={isOwned ? 'Active' : undefined}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all duration-150 ${
                                      isOwned
                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200 cursor-not-allowed'
                                        : isSelected
                                          ? 'bg-red-500 text-white border-red-500 hover:bg-red-600'
                                          : isHovered
                                            ? 'bg-red-100 text-red-700 border-red-300 shadow-sm'
                                            : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                                    }`}
                                  >
                                    {season}
                                  </button>
                                );
                              })}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer CTA */}
            <div className="mt-8 flex flex-col items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setContinueError(null);
                  
                  // Validate addon selection
                  const validationError = validateAddonSelection();
                  if (validationError) {
                    setContinueError(validationError);
                    return;
                  }
                  
                  if (!canContinueToCheckout) {
                    if (pricingLoading) {
                      setContinueError('Fetching pricing… please wait.');
                      return;
                    }
                    if (pricingUnavailable) {
                      setContinueError('Pricing unavailable, contact support.');
                      return;
                    }
                    setContinueError('Unable to continue. Please review your selection.');
                    return;
                  }
                  navigate(CHECKOUT, {
                    state: {
                      selectedSports: displayedSports,
                      addons,
                      addonMetaBySportId,
                      totalAddonCost,
                      basePriceBySportId,
                      currency,
                      isAddonOnly,
                    },
                  });
                }}
                className="px-10 py-3 font-semibold rounded-lg transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700"
              >
                Continue to Checkout
              </button>
              {continueError && <p className="text-red-600 text-sm font-medium">{continueError}</p>}
              <p className="text-slate-500 text-sm">
                {isAddonOnly 
                  ? 'Select at least one addon to proceed'
                  : missingAddonSelectionForSubscribed.length > 0
                    ? 'Select at least one season per addon to continue'
                    : 'You can choose StatCrew seasons later.'}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
